/**
 * PresidentsClubManager — admin panel for President's Club annual targets
 * (config/crmSettings.presidentsClub).
 *
 * The Leaderboard page shows every LO's YTD progress toward these targets
 * (qualify on volume OR units). Saved via the updateCrmSettings callable.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Icon,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  SimpleGrid,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { FiSave, FiStar } from 'react-icons/fi';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../../utils/init-firebase';
import { useCrmSettings } from '../../../contexts/CrmSettingsContext';

const DEFAULTS = {
  volumeTarget: 15000000,
  unitsTarget: 50,
};

export default function PresidentsClubManager() {
  const toast = useToast();
  const { presidentsClub } = useCrmSettings();
  const [form, setForm] = useState({ ...DEFAULTS });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const helperColor = useColorModeValue('gray.600', 'gray.400');

  useEffect(() => {
    if (presidentsClub && typeof presidentsClub === 'object') {
      setForm({ ...DEFAULTS, ...presidentsClub });
      setDirty(false);
    }
  }, [presidentsClub]);

  const numberField = (key, value) => {
    const num = Number(value);
    setForm((prev) => ({ ...prev, [key]: Number.isFinite(num) ? num : DEFAULTS[key] }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!(form.volumeTarget > 0) || !(form.unitsTarget > 0)) {
      toast({ title: 'Targets must be greater than zero', status: 'warning', duration: 4000 });
      return;
    }
    setSaving(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const updateCrmSettings = httpsCallable(functions, 'updateCrmSettings');
      await updateCrmSettings({
        updates: {
          presidentsClub: {
            volumeTarget: Number(form.volumeTarget),
            unitsTarget: Number(form.unitsTarget),
          },
        },
      });
      setDirty(false);
      toast({
        title: 'President\u2019s Club targets saved',
        description: 'The Leaderboard progress bars update on next load.',
        status: 'success',
        duration: 4000,
      });
    } catch (error) {
      console.error('Failed to save presidents club targets:', error);
      toast({ title: 'Save failed', description: error.message, status: 'error', duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <HStack spacing={2} mb={1}>
        <Icon as={FiStar} color="yellow.400" />
        <Heading size="md">President&apos;s Club</Heading>
      </HStack>
      <Text fontSize="sm" color={helperColor} mb={4}>
        Annual targets for President&apos;s Club. Loan officers qualify by hitting <strong>either</strong> the
        funded-volume or the units target for the calendar year.
      </Text>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} maxW="620px">
        <FormControl>
          <FormLabel fontSize="sm">Funded volume target ($/year)</FormLabel>
          <NumberInput size="sm" value={form.volumeTarget} min={1000000} step={1000000}
            onChange={(_, v) => numberField('volumeTarget', v)}>
            <NumberInputField />
            <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
          </NumberInput>
          <FormHelperText fontSize="xs">
            Currently ${(Number(form.volumeTarget) / 1e6).toFixed(1)}M
          </FormHelperText>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm">Units target (loans/year)</FormLabel>
          <NumberInput size="sm" value={form.unitsTarget} min={1} step={5}
            onChange={(_, v) => numberField('unitsTarget', v)}>
            <NumberInputField />
            <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
          </NumberInput>
          <FormHelperText fontSize="xs">Funded units in the calendar year</FormHelperText>
        </FormControl>
      </SimpleGrid>

      <VStack align="flex-start" mt={5}>
        <Button
          size="sm"
          colorScheme="yellow"
          leftIcon={<FiSave />}
          onClick={handleSave}
          isLoading={saving}
          isDisabled={!dirty}
        >
          Save President&apos;s Club targets
        </Button>
      </VStack>
    </Box>
  );
}
